import { FieldValues, useController, UseControllerProps } from 'react-hook-form';
import { CheckboxGroup, CheckboxGroupProps, MinOptionInfo } from '@stitch-fix/mode-react';

/**
 * Require options to include a `label` to prevent `Checkbox` from showing
 * the option `value` text if the option `label` is undefined.
 */
type TextOptionInfo = MinOptionInfo<string> & {
  label: string;
};
export type ControlledCheckboxGroupProps<TOption extends TextOptionInfo, TFieldValues extends FieldValues> = {
  /**
   * The `control` object returned by `useForm`.
   */
  control: UseControllerProps<TFieldValues>['control'];
  /**
   * A key name configured in the corresponding zod schema, i.e.,
   * pass `"selections"` if the zod schema is `z.object({ selections: ... })`.
   */
  name: UseControllerProps<TFieldValues>['name'];
  /**
   * An array of options. Each object must define a `value` and `label` field.
   */
  options: CheckboxGroupProps<string, TOption>['options'];
};

/**
 * This component allows [React Hook Form](https://react-hook-form.com/)
 * to easily communicate with Mode React's [CheckboxGroup](https://mode-react.daylight.stitchfix.com/?path=/docs/components-checkbox--group#group)
 */
export const ControlledCheckboxGroup = <TOption extends TextOptionInfo, TFieldValues extends FieldValues>({
  control,
  name,
  options
}: ControlledCheckboxGroupProps<TOption, TFieldValues>) => {
  const {
    field: {
      onChange,
      value
    }
  } = useController({
    control,
    name
  });
  if (!options) {
    return null;
  }
  return <CheckboxGroup name={name} options={options} value={value} onChange={onChange} direction="vertical" spacing={1.5} mb={2} />;
};